// The Xonera figure theme: one palette for every code-drawn visual (mermaid,
// Vega-Lite charts, function plots, Venn, number lines, labelled diagrams).
//
// Figures render on the white figure surface in BOTH app themes (see
// FIGURE_SURFACE in markdown.tsx), so these are fixed light-surface colours,
// not theme tokens. Every hue comes from the existing palette - the brand ramp
// (--brand-*) and the chart series (--chart-series-1..5, globals.css) - plus
// the warm neutrals of the illustration style (backend/data/visual-style).
// No new hues: memory "keep the existing palette".

export const FIGURE = {
  ink: '#17172B',
  muted: '#5B5E7A',
  rule: '#E3E5F5',
  axis: '#C9CCE0',
  paper: '#FFFFFF',
  wash: '#F7F8FF',
  // --chart-series-1..5 (indigo-600 → indigo-200), strongest first.
  series: ['#4F46E5', '#6366F1', '#818CF8', '#A5B4FC', '#C7D2FE'],
  brandDeep: '#2B2683',
  brandSoft: '#EEF0FF',
  warm: '#F5F1EA',
  warmEdge: '#D9D4CC',
  font: '"Inter Variable", Inter, system-ui, sans-serif',
  mono: '"JetBrains Mono Variable", "JetBrains Mono", ui-monospace, monospace',
} as const;

/** mermaid `initialize` theme: `base` with every variable from FIGURE. */
export const MERMAID_THEME = {
  theme: 'base' as const,
  themeVariables: {
    fontFamily: FIGURE.font,
    fontSize: '14px',
    background: FIGURE.paper,
    textColor: FIGURE.ink,
    primaryColor: FIGURE.brandSoft,
    primaryBorderColor: FIGURE.series[1],
    primaryTextColor: FIGURE.ink,
    secondaryColor: FIGURE.warm,
    secondaryBorderColor: FIGURE.warmEdge,
    secondaryTextColor: FIGURE.ink,
    tertiaryColor: FIGURE.paper,
    tertiaryBorderColor: FIGURE.rule,
    tertiaryTextColor: FIGURE.ink,
    lineColor: FIGURE.muted,
    clusterBkg: FIGURE.wash,
    clusterBorder: FIGURE.series[4],
    edgeLabelBackground: FIGURE.paper,
    noteBkgColor: FIGURE.warm,
    noteBorderColor: FIGURE.warmEdge,
    noteTextColor: FIGURE.ink,
    // Pie slices and timeline sections step down the series ramp.
    pie1: FIGURE.series[0],
    pie2: FIGURE.series[1],
    pie3: FIGURE.series[2],
    pie4: FIGURE.series[3],
    pie5: FIGURE.series[4],
    pieStrokeColor: FIGURE.paper,
    pieOuterStrokeColor: FIGURE.paper,
    cScale0: FIGURE.series[0],
    cScale1: FIGURE.series[1],
    cScale2: FIGURE.series[2],
    cScale3: FIGURE.series[3],
    cScale4: FIGURE.series[4],
    // Timeline section text and the rule under each event.
    cScaleLabel0: FIGURE.paper,
    cScaleLabel1: FIGURE.paper,
    cScaleLabel2: FIGURE.ink,
    cScaleLabel3: FIGURE.ink,
    cScaleLabel4: FIGURE.ink,
    cScaleInv0: FIGURE.series[0],
    cScaleInv1: FIGURE.series[1],
    cScaleInv2: FIGURE.series[2],
    cScaleInv3: FIGURE.series[3],
    cScaleInv4: FIGURE.series[4],
    cScalePeer0: FIGURE.series[0],
    cScalePeer1: FIGURE.series[1],
    cScalePeer2: FIGURE.series[2],
    cScalePeer3: FIGURE.series[3],
    cScalePeer4: FIGURE.series[4],
  },
};

/** Vega-Lite `config` for vega-embed. */
export const VEGA_CONFIG = {
  font: FIGURE.font,
  background: FIGURE.paper,
  view: { stroke: null },
  range: { category: [...FIGURE.series], ordinal: [...FIGURE.series] },
  mark: { color: FIGURE.series[0] },
  axis: {
    labelFont: FIGURE.mono,
    labelColor: FIGURE.muted,
    labelFontSize: 11,
    titleFont: FIGURE.font,
    titleColor: FIGURE.ink,
    titleFontWeight: 600 as const,
    gridColor: FIGURE.rule,
    domainColor: FIGURE.axis,
    tickColor: FIGURE.axis,
  },
  legend: {
    labelFont: FIGURE.font,
    labelColor: FIGURE.ink,
    titleFont: FIGURE.font,
    titleColor: FIGURE.ink,
  },
  title: { font: FIGURE.font, color: FIGURE.ink, fontWeight: 600 as const },
};

/** Default line colours for function plots, in series order. */
export function seriesColour(i: number): string {
  return FIGURE.series[i % FIGURE.series.length];
}

// Generated figures render on one WHITE surface in both app themes - always
// legible, reads as an intentional "figure" card, centres and width-caps the
// visual so it never overflows a compact card. `w-full` keeps the box sized
// inside a flex-centred parent (flashcard / mindspace answers).
export const FIGURE_SURFACE =
  'my-3 w-full max-w-full overflow-x-auto rounded-lg border border-border ' +
  'bg-white p-3 text-center [&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-w-full';
